import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import countryList from "react-select-country-list";
import useMapLocation from "../../hooks/useMapLocation";
import { Autocomplete, GoogleMap } from "@react-google-maps/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  useVendorById,
  useUpdateVendor,
  useUploadVendorProfileImage,
  useVendorCategories,
  useMyVendor,
  useUpdateMyVendor,
} from "../../hooks/useVendor";
import { useUser } from "../../hooks/useUser";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import SafeImage from "../../components/common/SafeImage";

function getProfileImageUrl(img) {
  if (!img) return "/avatar-placeholder.png";
  if (img.startsWith("/"))
    return `${import.meta.env.VITE_BACKEND_ORIGIN || "http://localhost:8000"}${img}`;
  return img;
}

export default function VendorProfileSettings() {
  const { data: user, isLoading } = useUser();
  const queryClient = useQueryClient();
  const [vendorInfo, setVendorInfo] = useState({
    firstName: "",
    description: "",
    vendorType: "",
    origin: "",
    address: "",
    email: "",
    phoneNumber: "",
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [countries] = useState(countryList().getData());
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({ longitude: "", latitude: "" });
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isLocationLoaded, setIsLocationLoaded] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const containerStyle = { width: "100%", height: "300px" };
  const centerDefault = { lat: 20.5937, lng: 78.9629 };
  const [mapCenter, setMapCenter] = useState(centerDefault);
  const mapRef = useRef(null);
  const {
    isLoaded,
    country,
    latLng,
    address: mapAddress,
    setAddress,
    setLatLng,
    onPlaceChanged,
    error: mapError,
    autocompleteRef,
  } = useMapLocation();
  const { data: categoriesResponse, isLoading: categoriesLoading } =
    useVendorCategories({
      staleTime: 5 * 60 * 1000, // Cache categories for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    });
  const categories = categoriesResponse?.data || [];
  const updateVendorMutation = useUpdateMyVendor({
    onSuccess: (result) => {
      if (result.success === true || result.status === "success") {
        toast.success(result.message || "Vendor updated successfully");
        queryClient.invalidateQueries(["vendor", user?.user_id]);
        queryClient.invalidateQueries(["vendors"]);
        refetchVendor(); // Use refetch instead of fetchVendorData
      } else {
        toast.error(result.message || "Failed to update vendor");
      }
    },
    onError: (error) => {
      const backendErrors = error?.response?.data?.errors;
      let firstErrorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update vendor";
      if (backendErrors) {
        setErrors((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(backendErrors).map(([key, value]) => [
              key === "name"
                ? "firstName"
                : key === "phone_no"
                  ? "phoneNumber"
                  : key,
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        }));
        const firstField = Object.keys(backendErrors)[0];
        const firstMsg = Array.isArray(backendErrors[firstField])
          ? backendErrors[firstField][0]
          : backendErrors[firstField];
        firstErrorMsg = firstMsg || firstErrorMsg;
      }
      toast.error(firstErrorMsg);
    },
    onSettled: () => setFormLoading(false),
  });
  const uploadImageMutation = useUploadVendorProfileImage({
    onSuccess: (result) => {
      if (result.success === true || result.status === "success") {
        queryClient.invalidateQueries(["vendor", user?.user_id]);
        toast.success("Profile image updated successfully");
        refetchVendor(); // Use refetch instead of fetchVendorData
      } else {
        toast.error(result.message || "Failed to update profile image");
      }
    },
    onError: (error) => {
      toast.error(
        "Failed to update profile image: " + (error.message || "Unknown error"),
      );
    },
    onSettled: () => setFormLoading(false),
    isVendor: true,
  });
  const {
    data: vendorResponse,
    isLoading: vendorLoading,
    refetch: refetchVendor,
  } = useMyVendor();
  const [errorShown, setErrorShown] = useState(false);
  useEffect(() => {
    if (user) {
      // fetchVendorData(); // Removed as per edit hint
    }
  }, [user]);
  useEffect(() => {
    console.log("vendorResponse:", vendorResponse);
    if (vendorResponse && vendorResponse.data) {
      const vendorData = vendorResponse.data;
      setVendorInfo({
        firstName: vendorData.user?.name || "",
        description: vendorData.description || "",
        vendorType:
          (vendorData.vendor_type_id || vendorData.vendor_type)?.toString() ||
          "",
        origin: vendorData.origin || "",
        address: vendorData.address || "",
        email: vendorData.user?.email || "",
        phoneNumber: vendorData.user?.phone_no || "",
        profile_image: vendorData.user?.profile_image || "",
      });
      // Profile image preview logic
      const img = vendorData.user?.profile_image;
      setProfileImagePreview(
        img && img.startsWith("http")
          ? img
          : img
            ? `${import.meta.env.VITE_BACKEND_ORIGIN || "http://localhost:8000"}${img}`
            : "",
      );
      if (
        (vendorData.latitude || vendorData.latitude === 0) &&
        (vendorData.longitude || vendorData.longitude === 0)
      ) {
        setFormData({
          latitude: vendorData.latitude.toString(),
          longitude: vendorData.longitude.toString(),
        });
        setMapCenter({
          lat: parseFloat(vendorData.latitude),
          lng: parseFloat(vendorData.longitude),
        });
        setLatLng({
          lat: parseFloat(vendorData.latitude),
          lng: parseFloat(vendorData.longitude),
        });
      }
    }
  }, [vendorResponse]);
  useEffect(() => {
    if (
      !isLoading &&
      vendorResponse &&
      vendorResponse.status !== "success" &&
      !errorShown
    ) {
      toast.error(vendorResponse.message || "Failed to fetch vendor data");
      setErrorShown(true);
    }
  }, [isLoading, vendorResponse, errorShown]);
  useEffect(() => {
    if (latLng) {
      setMapCenter(latLng);
    }
  }, [latLng]);
  // Remove fetchVendorData and all its usages
  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setVendorInfo((prevInfo) => ({ ...prevInfo, [name]: value }));
  };
  const handlePhoneChange = (value) => {
    setVendorInfo((prevInfo) => ({ ...prevInfo, phoneNumber: value }));
  };
  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };
  const handleLocationSelect = () => {
    if (mapCenter) {
      setFormData((prev) => ({
        ...prev,
        longitude: mapCenter.lng.toString(),
        latitude: mapCenter.lat.toString(),
      }));
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: mapCenter }, (results, status) => {
          if (status === "OK" && results[0]) {
            setAddress(results[0].formatted_address);
            setVendorInfo((prev) => ({
              ...prev,
              address: results[0].formatted_address,
            }));
          }
        });
      }
      setShowLocationModal(false);
      setIsLocationLoaded(true);
      if (errors.location) {
        setErrors((prev) => ({ ...prev, location: "" }));
      }
    }
  };
  const handleOpenLocationModal = () => {
    setShowLocationModal(true);
    setTouched((prev) => ({ ...prev, location: true }));
    if (!latLng) {
      setLatLng(centerDefault);
    }
  };
  const handleGetCurrentLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLatLng({ lat: latitude, lng: longitude });
          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode(
              { location: { lat: latitude, lng: longitude } },
              (results, status) => {
                if (status === "OK" && results[0]) {
                  setAddress(results[0].formatted_address);
                }
                setLocationLoading(false);
              },
            );
          } else {
            setLocationLoading(false);
          }
        },
        (error) => {
          toast.error(
            "Unable to fetch your location. Please allow location access.",
          );
          setLocationLoading(false);
        },
      );
    } else {
      toast.error("Geolocation is not supported by your browser.");
      setLocationLoading(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      vendorType: true,
      origin: true,
      address: true,
      email: true,
      phoneNumber: true,
      location: true,
    });

    const validationErrors = validateForm();
    if (Object.values(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setFormLoading(true);

    try {
      const payload = {
        name: vendorInfo.firstName,
        email: vendorInfo.email,
        phone_no: vendorInfo.phoneNumber,
        vendor_type: vendorInfo.vendorType,
        origin: vendorInfo.origin,
        address: vendorInfo.address,
        description: vendorInfo.description,
        longitude: formData.longitude,
        latitude: formData.latitude,
      };

      // Update profile first
      await updateVendorMutation.mutateAsync({ payload });

      // Then upload image if present
      if (profileImage && profileImage.name && profileImage.size) {
        await uploadImageMutation.mutateAsync({ file: profileImage });
      }
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setFormLoading(false);
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!vendorInfo.firstName) newErrors.firstName = "Shop name is required";
    if (!vendorInfo.vendorType)
      newErrors.vendorType = "Vendor type is required";
    if (!vendorInfo.origin) newErrors.origin = "Origin is required";
    if (!vendorInfo.address) newErrors.address = "Address is required";
    if (!vendorInfo.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vendorInfo.email))
      newErrors.email = "Please enter a valid email address";
    if (!vendorInfo.phoneNumber)
      newErrors.phoneNumber = "Phone number is required";
    else if (!/^\+?[1-9]\d{7,14}$/.test(vendorInfo.phoneNumber))
      newErrors.phoneNumber = "Please enter a valid international phone number";
    if (!formData.longitude || !formData.latitude)
      newErrors.location = "Business location is required";
    return newErrors;
  };
  // Debounced validation to improve performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setErrors(validateForm());
    }, 300); // Debounce validation by 300ms

    return () => clearTimeout(timeoutId);
  }, [vendorInfo, formData]);
  if (isLoading) return <div>Loading...</div>;
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="row">
            <div className="col-sm-8 m-auto">
              <div className="card">
                <div className="card-body">
                  <div className="title-header option-title">
                    <h5>Profile Settings</h5>
                  </div>
                  <form
                    className="theme-form theme-form-2 mega-form"
                    onSubmit={handleSubmit}
                  >
                    <div className="row">
                      <div className="mb-4 row align-items-center">
                        <label className="form-label-title col-lg-2 col-md-3 mb-0">
                          Shop Name <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <input
                            className="form-control"
                            type="text"
                            name="firstName"
                            value={vendorInfo.firstName}
                            onChange={handleInfoChange}
                            required
                          />
                          {touched.firstName && errors.firstName && (
                            <div className="text-danger small mt-1">
                              {errors.firstName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="form-label-title col-lg-2 col-md-3 mb-0">
                          Description
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <input
                            className="form-control"
                            type="text"
                            name="description"
                            value={vendorInfo.description}
                            onChange={handleInfoChange}
                          />
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="form-label-title col-lg-2 col-md-3 mb-0">
                          Vendor Type <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-9 col-lg-10">
                          {categoriesLoading ? (
                            <div className="form-control">
                              Loading categories...
                            </div>
                          ) : (
                            <>
                              <select
                                className="form-select"
                                name="vendorType"
                                value={vendorInfo.vendorType}
                                onChange={handleInfoChange}
                                required
                              >
                                <option value="">Select Vendor Type</option>
                                {categories?.map((cat) => (
                                  <option
                                    key={cat.category_id}
                                    value={cat.category_id}
                                  >
                                    {cat.category_name}
                                  </option>
                                ))}
                              </select>
                              {touched.vendorType && errors.vendorType && (
                                <div className="text-danger small mt-1">
                                  {errors.vendorType}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="form-label-title col-lg-2 col-md-3 mb-0">
                          Origin <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <select
                            className="form-select"
                            name="origin"
                            value={vendorInfo.origin}
                            onChange={handleInfoChange}
                            required
                          >
                            <option value="">Select Country</option>
                            {countries?.map((country) => (
                              <option key={country.value} value={country.label}>
                                {country.label}
                              </option>
                            ))}
                          </select>
                          {touched.origin && errors.origin && (
                            <div className="text-danger small mt-1">
                              {errors.origin}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="form-label-title col-lg-2 col-md-3 mb-0">
                          Address <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <input
                            className="form-control"
                            type="text"
                            name="address"
                            value={vendorInfo.address}
                            onChange={handleInfoChange}
                            required
                          />
                          {touched.address && errors.address && (
                            <div className="text-danger small mt-1">
                              {errors.address}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="form-label-title col-lg-2 col-md-3 mb-0">
                          Business Location
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <div
                            className={`form-control text-start${touched.location && errors.location ? " is-invalid" : ""}`}
                            style={{
                              backgroundColor: "white",
                              cursor: formLoading ? "not-allowed" : "pointer",
                              minHeight: "45px",
                              display: "flex",
                              alignItems: "center",
                              padding: "0 12px",
                            }}
                            tabIndex={0}
                            onClick={handleOpenLocationModal}
                            role="button"
                            aria-disabled={formLoading}
                          >
                            <i className="fa fa-map-marker-alt text-primary me-2"></i>
                            <span
                              className={
                                formData.longitude && formData.latitude
                                  ? "text-dark small"
                                  : "text-muted small"
                              }
                              style={{ fontWeight: 400 }}
                            >
                              {formData.longitude && formData.latitude
                                ? "Location Selected"
                                : "Click to select your business location"}
                            </span>
                          </div>
                          {touched.location && errors.location && (
                            <div className="invalid-feedback d-block">
                              {errors.location}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="col-lg-2 col-md-3 col-form-label form-label-title">
                          Email Address <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <input
                            className="form-control"
                            type="email"
                            name="email"
                            value={vendorInfo.email}
                            onChange={handleInfoChange}
                            required
                          />
                          {touched.email && errors.email && (
                            <div className="text-danger small mt-1">
                              {errors.email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="col-lg-2 col-md-3 col-form-label form-label-title">
                          Phone Number <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <PhoneInput
                            country={"us"}
                            value={vendorInfo.phoneNumber}
                            onChange={handlePhoneChange}
                            inputProps={{
                              name: "phoneNumber",
                              required: true,
                              className: "form-control",
                              onBlur: () =>
                                setTouched((prev) => ({
                                  ...prev,
                                  phoneNumber: true,
                                })),
                            }}
                            containerStyle={{ width: "100%" }}
                            inputStyle={{
                              width: "100%",
                              height: 45,
                              fontSize: "1rem",
                            }}
                            dropdownStyle={{ zIndex: 9999 }}
                          />
                          {touched.phoneNumber && errors.phoneNumber && (
                            <div className="text-danger small mt-1">
                              {errors.phoneNumber}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="col-lg-2 col-md-3 col-form-label form-label-title">
                          Profile Image
                        </label>
                        <div className="col-md-9 col-lg-10 d-flex align-items-center">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={handleProfileImageChange}
                          />
                          <SafeImage
                            src={getProfileImageUrl(profileImagePreview)}
                            alt="Profile Preview"
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: "50%",
                              marginLeft: 16,
                            }}
                            fallbackSrc="/avatar-placeholder.png"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-submit-button">
                      <button
                        className="btn btn-animation ms-auto"
                        type="submit"
                        disabled={
                          formLoading || Object.values(errors).length > 0
                        }
                        style={{
                          backgroundColor:
                            formLoading || Object.values(errors).length > 0
                              ? "#6c757d"
                              : "",
                          color:
                            formLoading || Object.values(errors).length > 0
                              ? "#ffffff"
                              : "",
                          cursor:
                            formLoading || Object.values(errors).length > 0
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            formLoading || Object.values(errors).length > 0
                              ? 0.6
                              : 1,
                        }}
                      >
                        {formLoading ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add Change Password Form below profile form */}
      <div className="row mt-4">
        <div className="col-sm-8 m-auto">
          <ChangePasswordForm endpoint="/vendor/change-password" />
        </div>
      </div>
      {showLocationModal && isLoaded && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowLocationModal(false)}
          ></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="locationModalLabel"
            aria-modal="true"
            style={{ background: "rgba(0,0,0,0.1)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title" id="locationModalLabel">
                      Choose your Business Location
                    </h5>
                    <h6 className="mb-0 text-muted">{country}</h6>
                    <p
                      className="mt-1 text-muted mb-0"
                      style={{ fontSize: "0.95rem" }}
                    >
                      Enter your address and we will specify the offer for your
                      area.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowLocationModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="input-group">
                      <span
                        className="input-group-text bg-white border-end-0"
                        style={{ paddingRight: 0 }}
                      >
                        <i className="fa fa-search text-muted"></i>
                      </span>
                      {isLoaded && (
                        <Autocomplete
                          onLoad={(ref) => (autocompleteRef.current = ref)}
                          onPlaceChanged={onPlaceChanged}
                        >
                          <input
                            type="text"
                            placeholder="Search address"
                            className="form-control border-start-0"
                            style={{ maxWidth: 300 }}
                            value={mapAddress}
                            onChange={(e) => setAddress(e.target.value)}
                          />
                        </Autocomplete>
                      )}
                      <button
                        onClick={handleLocationSelect}
                        className="btn btn-primary ms-2 d-flex align-items-center gap-2"
                        disabled={!mapCenter}
                        type="button"
                      >
                        <i className="fas fa-map-marker-alt"></i>
                        Set Location
                      </button>
                      {locationLoading && (
                        <div style={{ textAlign: "center", margin: "1rem 0" }}>
                          <div className="spinner-border" role="status" />
                          <div>Fetching your current location...</div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn ms-2 d-flex align-items-center gap-2"
                        onClick={handleGetCurrentLocation}
                        style={{ minWidth: 140 }}
                        disabled={locationLoading}
                      >
                        <i
                          className="fa fa-location-arrow me-1"
                          style={{ opacity: 0.7 }}
                        ></i>
                        {locationLoading
                          ? "Locating..."
                          : "Get Current Location"}
                      </button>
                    </div>
                  </div>
                  {mapAddress && (
                    <div className="mb-2">
                      <p className="mb-0">
                        <strong>Address:</strong> {mapAddress}
                      </p>
                    </div>
                  )}
                  {mapError && (
                    <p className="text-danger small mb-2">{mapError}</p>
                  )}
                  <div className="mb-2">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: 300,
                      }}
                    >
                      <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={latLng ? 15 : 6}
                        onLoad={(map) => (mapRef.current = map)}
                        onDragEnd={() => {
                          if (mapRef.current) {
                            const newCenter = mapRef.current.getCenter();
                            setMapCenter({
                              lat: newCenter.lat(),
                              lng: newCenter.lng(),
                            });
                          }
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          transform: "translate(-50%, -100%)",
                          pointerEvents: "none",
                          zIndex: 2,
                        }}
                      >
                        <i
                          className="fas fa-map-marker-alt"
                          style={{ fontSize: 36, color: "#d00" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowLocationModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleLocationSelect}
                    disabled={!mapCenter}
                  >
                    Save Location
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
